import { useFetcher } from '@remix-run/react'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Checkbox, FormInput, Heading } from '~/components'
import { reCAPTCHA } from '~/hooks'
import { action } from '~/routes/api.mailchimp-form'
import type { formErrors } from '~/types'
import { getGlobal } from '~/utils'

type fieldsType = {
  firstName?: string
  lastName?: string
  email?: string
  travelAgent?: boolean
  abta?: string
}

const Newsletter = () => {
  const fetcher = useFetcher<typeof action>()

  // env
  const { env } = getGlobal()
  const recaptcha = new reCAPTCHA(env.RECAPTCHA_SITE_KEY, 'form')

  // Form Fields
  const [fields, setFields] = useState<fieldsType>({})

  // Response
  const [response, setResponse] = useState<{
    message: string
    type: 'loading' | 'success' | 'error'
  } | null>(null)

  // Form errors
  const [errors, setErrors] = useState<formErrors>({})

  const handleChange = (name: string, value: string | number | boolean) => {
    setFields({
      ...fields,
      [name]: value,
    })
  }

  const handleSubmit = async () => {
    setResponse({ message: 'Your form is sending.', type: 'loading' })
    const formData = new FormData()

    // check for errors with formdata
    setErrors({})
    const newErrors: formErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!fields.firstName) newErrors.firstName = 'first name required'
    if (!fields.lastName) newErrors.lastName = 'last name required'
    if (!fields.email) {
      if (fields.email && !emailRegex.test(fields.email)) {
        newErrors.email = 'invalid email format'
      }
      newErrors.email = 'email address required'
    }
    if (fields.travelAgent && !fields.abta) newErrors.abta = 'ABTA required'
    setErrors(newErrors)

    if (Object.keys(newErrors).length >= 1) {
      setResponse({ message: 'Please check your form.', type: 'error' })
      return null
    }

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string)
    })

    formData.append('newsletterSignup', 'true')

    if (Object.keys(newErrors).length === 0) {
      // captcha here
      const token = await recaptcha.getToken().catch(error => {
        console.log(error)
      })

      if (token) {
        formData.append('captchaToken', token)
        fetcher.submit(formData, {
          method: 'post',
          action: '/api/mailchimp-form',
        })
      }
    }
  }

  // On receiving form response
  useEffect(() => {
    if (fetcher.data?.newsletter) {
      if (fetcher.data?.newsletter.error === 'undefined') {
        setResponse({
          message: 'Somethings gone wrong, try again later.',
          type: 'error',
        })
      }
      if (fetcher.data?.newsletter?.error === 'none') {
        setResponse({
          message: 'Thank you for getting in touch.',
          type: 'success',
        })
      } else if (
        JSON.parse(fetcher.data?.newsletter?.mailchimpResponse?.response.text)
          .title == 'Member Exists'
      ) {
        setResponse({
          message: 'The email address is already subscribed',
          type: 'loading',
        })
      }
      if (fetcher.data?.newsletter?.response == false) {
        setResponse({
          message: 'Oops! It looks like our bot detectors have ',
          type: 'error',
        })
      }
    }
  }, [fetcher.data?.newsletter])
  return (
    <div className="bg-teal-700 py-10">
      <div className="container flex w-full flex-col items-center justify-center lg:w-2/5">
        <Heading
          tag="h4"
          size="h4"
          className="text-beige-200"
        >
          Sign up to our newsletter
        </Heading>

        <fetcher.Form
          onSubmit={e => {
            e.preventDefault
            handleSubmit()
          }}
          className="w-full"
        >
          {response && (
            <p
              className={clsx({
                'text-red-500': response.type == 'error',
                'text-green-500': response.type == 'success',
                'text-blue-500': response.type == 'loading',
              })}
            >
              {response.message}
            </p>
          )}
          <div className="grid w-full grid-cols-1 gap-2 text-beige-200 lg:gap-4">
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-4">
              <input
                type="hidden"
                value="newsletterSignup"
              />
              <FormInput
                id="newsletterfirstname"
                name="firstname"
                placeholder="First name"
                autocomplete="firstname"
                error={errors.firstName}
                onChange={e => handleChange('firstName', e)}
              />
              <FormInput
                id="newsletterlastname"
                name="lastname"
                placeholder="Last name"
                autocomplete="last name"
                error={errors.lastName}
                onChange={e => handleChange('lastName', e)}
              />
            </div>
            <FormInput
              id="newsletteremail"
              name="email"
              placeholder="Email address"
              autocomplete="email"
              type="email"
              inputMode="email"
              error={errors.email}
              onChange={e => handleChange('email', e)}
            />
            <div className="py-4 text-center">
              <Checkbox
                textSize="text-xs"
                label="Tick here if you are a travel agent"
                id="newslettertravelAgent"
                center
                onChange={e => {
                  handleChange('travelAgent', e.target.checked)
                }}
              />
            </div>
            <div
              className={clsx('mb-4', {
                hidden: !fields.travelAgent,
              })}
            >
              <FormInput
                id="newsletterabta"
                name="abta"
                placeholder="What's your ABTA number?"
                error={errors.abta}
                onChange={e => handleChange('abta', e)}
              />
            </div>
            <div className="mx-auto">
              <input
                className="border border-transparent bg-gold-500 px-8 py-3 text-center text-beige-200 hover:border-gold-300 hover:bg-gold-300 hover:text-beige-200"
                type="submit"
              />
            </div>
          </div>
        </fetcher.Form>
      </div>
    </div>
  )
}

export default Newsletter
